import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Search,
  MapPin,
  Calendar,
  Clock,
  DollarSign,
  Users,
  Navigation2,
  CheckCircle2,
  ExternalLink,
  ChevronRight,
  Filter
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { OrdemDeServico } from '../../types/ordemServico';
import { sanitizeText } from '../../utils/textSanitizer';
import { formatDateBR } from '../../utils/dateUtils';

export const AreaMotoristaMinhasViagens: React.FC = () => {
  const { ordensServico, setViagemAtiva, isAgregado, isLoading } = useAreaMotorista();
  const navigate = useNavigate();

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todas' | 'hoje' | 'em_aberto' | 'concluidas'>('todas');

  const hojeIso = new Date().toISOString().split('T')[0];
  const hojeLocal = new Date().toLocaleDateString('sv-SE');

  const viagensFiltradas = useMemo(() => {
    return ordensServico.filter((os) => {
      if (os.status_id === 3) return false; // Ignora canceladas
      const dataOS = os.data_atendimento ? os.data_atendimento.split('T')[0] : (os.created_at ? os.created_at.split('T')[0] : '');
      const isConcluida = os.status_id === 4 || !!os.assinatura;

      const isHoje = dataOS === hojeIso || dataOS === hojeLocal;

      if (filtroStatus === 'hoje' && !isHoje) return false;
      if (filtroStatus === 'em_aberto' && isConcluida) return false;
      if (filtroStatus === 'concluidas' && !isConcluida) return false;

      // Filtro de busca textual
      if (busca.trim()) {
        const termo = busca.toLowerCase();
        const matchId = String(os.id).includes(termo);
        const matchDesc = (os.descricao || '').toLowerCase().includes(termo);
        const matchRetirada = (os.retirada || '').toLowerCase().includes(termo);
        const matchEntrega = (os.entrega || '').toLowerCase().includes(termo);

        if (!matchId && !matchDesc && !matchRetirada && !matchEntrega) {
          return false;
        }
      }

      return true;
    });
  }, [ordensServico, busca, filtroStatus, hojeIso, hojeLocal]);

  const handleOpenExecution = (os: OrdemDeServico) => {
    setViagemAtiva(os);
    navigate('/area-motorista/execucao');
  };

  const handleOpenMaps = (endereco?: string) => {
    if (!endereco) return;
    const encoded = encodeURIComponent(endereco);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  return (
    <div className="space-y-6 text-left">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-red-500" />
            <span>Minhas Viagens & Rotas</span>
          </h1>
          <p className="text-xs text-slate-400">
            Gerencie todas as ordens de serviço atribuídas a você.
          </p>
        </div>

        <span className="px-3 py-1 rounded-xl bg-slate-800 text-xs font-bold text-slate-300 self-start border border-slate-700">
          {viagensFiltradas.length} {viagensFiltradas.length === 1 ? 'viagem encontrada' : 'viagens encontradas'}
        </span>
      </div>

      {/* Search & Filter Bar */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar por número da OS, endereço de coleta, entrega ou carga..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-slate-950/70 border border-slate-800 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-xs text-white placeholder-slate-500 outline-none transition"
          />
        </div>

        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {[
            { id: 'todas', label: 'Todas as Viagens' },
            { id: 'hoje', label: '📅 Hoje' },
            { id: 'em_aberto', label: '⏳ Em Aberto / Rota' },
            { id: 'concluidas', label: '✅ Concluídas' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFiltroStatus(tab.id as any)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition ${
                filtroStatus === tab.id
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trip Cards List */}
      {isLoading ? (
        <div className="p-12 text-center text-xs text-slate-500">Carregando viagens do motorista...</div>
      ) : viagensFiltradas.length === 0 ? (
        <div className="p-12 text-center rounded-2xl border border-slate-800 bg-slate-950/50 space-y-3">
          <ClipboardList className="w-10 h-10 mx-auto text-slate-600 stroke-[1.5]" />
          <h3 className="text-sm font-bold text-slate-300">Nenhuma viagem encontrada</h3>
          <p className="text-xs text-slate-500">Tente ajustar os filtros ou os termos da busca.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {viagensFiltradas.map((os) => {
            const isConcluida = os.status_id === 4 || !!os.assinatura;
            const repasseValor = Number(os.repasse) || 0;
            const dataFormatada = os.data_atendimento
              ? formatDateBR(os.data_atendimento)
              : 'Data não informada';

            return (
              <div
                key={os.id}
                className="rounded-2xl border border-slate-800 bg-slate-950/70 hover:border-slate-700 p-4 sm:p-5 transition shadow-lg space-y-4"
              >
                {/* Top Info Bar */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/80 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-black text-white">OS #{os.id}</span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        isConcluida
                          ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                          : 'bg-amber-950 text-amber-300 border border-amber-800'
                      }`}
                    >
                      {isConcluida ? 'Concluída' : 'Em Aberto / Executar'}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{dataFormatada}</span>
                    </div>
                    {os.horario_inicio && (
                      <div className="flex items-center gap-1 text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{os.horario_inicio}</span>
                      </div>
                    )}
                    {isAgregado ? (
                      <span className="font-mono font-bold text-emerald-400 text-sm">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(repasseValor)}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-lg bg-blue-950/60 text-blue-300 border border-blue-800 text-[11px] font-semibold">
                        Frota Própria / CLT
                      </span>
                    )}
                  </div>
                </div>

                {/* Addresses (Coleta & Entrega) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  
                  {/* Coleta */}
                  <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800/80 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        Origem / Coleta
                      </span>
                      {os.retirada && (
                        <button
                          type="button"
                          onClick={() => handleOpenMaps(os.retirada)}
                          className="text-[10px] text-slate-400 hover:text-blue-400 flex items-center gap-0.5"
                        >
                          <span>Maps</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-slate-200 font-medium line-clamp-2">
                      {sanitizeText(os.retirada) || 'Não especificado'}
                    </p>
                  </div>

                  {/* Entrega */}
                  <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800/80 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        Destino / Entrega
                      </span>
                      {os.entrega && (
                        <button
                          type="button"
                          onClick={() => handleOpenMaps(os.entrega)}
                          className="text-[10px] text-slate-400 hover:text-emerald-400 flex items-center gap-0.5"
                        >
                          <span>Maps</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-slate-200 font-medium line-clamp-2">
                      {sanitizeText(os.entrega) || 'Não especificado'}
                    </p>
                  </div>
                </div>

                {/* Cargo Details & Helpers */}
                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400 pt-1">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span>
                      Carga: <strong className="text-slate-200">{sanitizeText(os.descricao) || 'Carga Padrão'}</strong>
                    </span>
                    {Number(os.quantidade_ajudante) > 0 && (
                      <span className="flex items-center gap-1 text-slate-300">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <strong>{os.quantidade_ajudante}</strong> {Number(os.quantidade_ajudante) === 1 ? 'Ajudante' : 'Ajudantes'}
                      </span>
                    )}
                  </div>

                  {/* Execution Action Button */}
                  <button
                    onClick={() => handleOpenExecution(os)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition shadow ${
                      isConcluida
                        ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700'
                        : 'bg-red-600 hover:bg-red-700 text-white shadow-red-600/30'
                    }`}
                  >
                    {isConcluida ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span>Ver Comprovante</span>
                      </>
                    ) : (
                      <>
                        <Navigation2 className="w-4 h-4" />
                        <span>Executar Viagem</span>
                      </>
                    )}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
